import React, { useState } from "react";
import {
  Stack,
  Group,
  Text,
  ActionIcon,
  TextInput,
  Box,
  ColorInput,
  Textarea,
  Button,
} from "@mantine/core";
import { IconChevronLeft, IconTrash, IconPlus } from "@tabler/icons-react";
import CustomRichTextEditor from "../../common/RichTextEditor";
import {
  hasFieldError,
  getFieldErrorMessage,
  getInputStyles,
  getLabelStyles,
  getLabelText,
} from "../utils/validation";

const COLOR_TEXT_BLACK = "#000000";
const COLOR_BACKGROUND_WHITE = "#FFFFFF";

interface AnnouncementBannerContent {
  sectionName?: string;
  body?: string;
  textColor?: string;
  backgroundColor?: string;
  items?: string[]; // Array of strings for announcement_modal.items
  callToAction?: string; // Call to action text
}

interface WebsiteGeneratorAnnouncementBannerProps {
  announcementBannerContent?: AnnouncementBannerContent;
  onContentChange: (content: AnnouncementBannerContent) => void;
  onBack: () => void;
  missingFields?: string[];
  requiredValidation?: { [field: string]: boolean };
}

const WebsiteGeneratorAnnouncementBanner: React.FC<
  WebsiteGeneratorAnnouncementBannerProps
> = ({
  announcementBannerContent,
  onContentChange,
  onBack,
  missingFields = [],
  requiredValidation = {},
}) => {
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  const handleSectionNameChange = (value: string) => {
    if (value.length > 100) {
      setValidationErrors((prev) => ({
        ...prev,
        sectionName: "Section name cannot exceed 100 characters",
      }));
      return;
    }
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next.sectionName;
      return next;
    });
    onContentChange({
      ...(announcementBannerContent || {}),
      sectionName: value,
    });
  };

  const handleBodyChange = (value: string) => {
    const wordCount = value
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0).length;
    if (wordCount > 250) {
      setValidationErrors((prev) => ({
        ...prev,
        body: "Body cannot exceed 250 words",
      }));
      return;
    }
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next.body;
      return next;
    });
    onContentChange({
      ...(announcementBannerContent || {}),
      body: value,
    });
  };

  const handleTextColorChange = (value: string) => {
    onContentChange({
      ...(announcementBannerContent || {}),
      textColor: value,
    });
  };

  const handleBackgroundColorChange = (value: string) => {
    onContentChange({
      ...(announcementBannerContent || {}),
      backgroundColor: value,
    });
  };

  const handleItemChange = (index: number, value: string) => {
    const currentItems = announcementBannerContent?.items || [];
    const updatedItems = [...currentItems];
    updatedItems[index] = value;
    onContentChange({
      ...(announcementBannerContent || {}),
      items: updatedItems,
    });
  };

  const handleAddItem = () => {
    const currentItems = announcementBannerContent?.items || [];
    onContentChange({
      ...(announcementBannerContent || {}),
      items: [...currentItems, ""],
    });
  };

  const handleRemoveItem = (index: number) => {
    const currentItems = announcementBannerContent?.items || [];
    const updatedItems = currentItems.filter((_, i) => i !== index);
    onContentChange({
      ...(announcementBannerContent || {}),
      items: updatedItems,
    });
  };

  const handleCallToActionChange = (value: string) => {
    onContentChange({
      ...(announcementBannerContent || {}),
      callToAction: value,
    });
  };

  const getWordCount = (text?: string) => {
    if (!text) return 0;
    return text
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0).length;
  };

  return (
    <Stack gap="md">
      {/* Header */}
      <Group justify="space-between" align="center">
        <ActionIcon variant="subtle" onClick={onBack}>
          <IconChevronLeft size={16} />
        </ActionIcon>
        <Text fw={600} size="lg">
          Announcement Banner
        </Text>
      </Group>

      {/* Section Name */}
      <Box>
        <Text
          size="sm"
          fw={500}
          mb="xs"
          style={getLabelStyles(
            "sectionName",
            requiredValidation,
            announcementBannerContent?.sectionName,
          )}
        >
          {getLabelText(
            "Section Name",
            "sectionName",
            requiredValidation,
            announcementBannerContent?.sectionName,
          )}
        </Text>
        <TextInput
          placeholder="Announcement Banner"
          value={announcementBannerContent?.sectionName || ""}
          onChange={(event) =>
            handleSectionNameChange(event.currentTarget.value)
          }
          error={getFieldErrorMessage(
            "sectionName",
            requiredValidation,
            validationErrors,
            announcementBannerContent?.sectionName,
          )}
          maxLength={100}
          styles={getInputStyles(
            "sectionName",
            requiredValidation,
            missingFields,
            undefined,
            announcementBannerContent?.sectionName,
          )}
        />
      </Box>

      {/* Body */}
      <Box>
        <Text
          size="sm"
          fw={500}
          mb="xs"
          style={getLabelStyles(
            "body",
            requiredValidation,
            announcementBannerContent?.body,
          )}
        >
          {getLabelText(
            "Body",
            "body",
            requiredValidation,
            announcementBannerContent?.body,
          )}
        </Text>
        <CustomRichTextEditor
          label=""
          placeholder="Enter announcement text..."
          value={announcementBannerContent?.body || ""}
          onChange={(htmlValue) => handleBodyChange(htmlValue)}
          minHeight={180}
        />
        {hasFieldError(
          "body",
          requiredValidation,
          announcementBannerContent?.body,
        ) && (
          <Text size="xs" c="red" mt="xs">
            Required
          </Text>
        )}
        {validationErrors.body && (
          <Text size="xs" c="red" mt="xs">
            {validationErrors.body}
          </Text>
        )}
        <Text size="xs" c="dimmed" mt="xs">
          {getWordCount(announcementBannerContent?.body)} words
        </Text>
      </Box>

      {/* Text Color */}
      <Box>
        <Text size="sm" fw={500} mb="xs">
          Text Color
        </Text>
        <ColorInput
          value={announcementBannerContent?.textColor || COLOR_TEXT_BLACK}
          onChange={handleTextColorChange}
          placeholder={COLOR_TEXT_BLACK}
        />
      </Box>

      {/* Background Color */}
      <Box>
        <Text size="sm" fw={500} mb="xs">
          Background Color
        </Text>
        <ColorInput
          value={
            announcementBannerContent?.backgroundColor || COLOR_BACKGROUND_WHITE
          }
          onChange={handleBackgroundColorChange}
          placeholder={COLOR_BACKGROUND_WHITE}
        />
      </Box>

      {/* Items (Array of Strings) */}
      <Box>
        <Group justify="space-between" mb="xs">
          <Text size="sm" fw={500}>
            Items
          </Text>
          <Button
            size="xs"
            variant="light"
            leftSection={<IconPlus size={14} />}
            onClick={handleAddItem}
          >
            Add Item
          </Button>
        </Group>
        {(announcementBannerContent?.items || []).map((item, index) => (
          <Group key={index} mb="xs" align="flex-start">
            <Textarea
              placeholder="Enter item text..."
              value={item}
              onChange={(event) =>
                handleItemChange(index, event.currentTarget.value)
              }
              style={{ flex: 1 }}
              minRows={2}
              maxRows={4}
            />
            <ActionIcon
              variant="subtle"
              color="red"
              onClick={() => handleRemoveItem(index)}
              mt="xs"
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        ))}
        {(!announcementBannerContent?.items ||
          announcementBannerContent.items.length === 0) && (
          <Text size="xs" c="dimmed" mb="xs">
            No items added. Click "Add Item" to add items.
          </Text>
        )}
      </Box>

      {/* Call to Action */}
      <Box>
        <Text size="sm" fw={500} mb="xs">
          Call to Action
        </Text>
        <TextInput
          placeholder="Enter call to action text..."
          value={announcementBannerContent?.callToAction || ""}
          onChange={(event) =>
            handleCallToActionChange(event.currentTarget.value)
          }
          maxLength={200}
        />
      </Box>
    </Stack>
  );
};

export default WebsiteGeneratorAnnouncementBanner;
